import { Controller } from "@hotwired/stimulus"
import { playChime, timerChimeEnabled, unlockChime } from "../utils/zen_chime"

// Zen timer: one header button, a few modes picked from a small dropdown.
//   pomodoro   — 25 min work + 5 min break, then back to idle
//   focus      — a plain countdown block (15/45/60 min)
//   meditation — countdown with a breathing overlay (4-4-4 box pacing)
//   wimhof     — rounds of power breaths, a breath-hold, then a recovery hold
// Completed work/meditation/wimhof blocks POST to /zen/sessions; pomodoro
// keeps the existing started/completed milestone pings. A bell marks the end
// of every block (utils/zen_chime) unless the menu's switch turns it off.
// Countdown state
// survives reloads via localStorage. The scene stays untouched unless a
// timer is running.
const POMODORO_WORK_MS = 25 * 60 * 1000
const POMODORO_BREAK_MS = 5 * 60 * 1000
const STORAGE_KEY = "zen-timer-state"
const TICK_MS = 250
const BREATH_PHASES = ["Breathe in", "Hold", "Breathe out"]
const BREATH_STEP_MS = 4000
const MEDITATION_FINISH_COPY = "Gently open your eyes."
const FINISH_LINGER_MS = 2600
// Matches r=54 in the overlay's SVG progress ring.
const RING_CIRCUMFERENCE = 2 * Math.PI * 54
// Wim Hof round: ~30 power breaths at a steady cadence, then a full-exhale
// breath-hold (retention, counted up until you tap to continue), then a
// short recovery hold. WIMHOF_BREATH_MS matches the zen-wim-breathe keyframe.
const WIMHOF_ROUNDS = 3
const WIMHOF_BREATHS = 30
const WIMHOF_BREATH_MS = 2200
const WIMHOF_RECOVERY_MS = 15 * 1000
const WIMHOF_PHASE_COPY = {
    breaths: "Power breaths",
    retention: "Hold - empty lungs",
    recovery: "Recovery hold"
}
const WIMHOF_FINISH_COPY = "Rounds complete. Breathe normally."

export default class extends Controller {
    static targets = ["label", "icon", "trigger", "picker", "controls", "status", "pauseButton", "overlay", "breathPhase", "breathRemaining", "sessionOverlay", "ringProgress", "sessionRemaining", "sessionMode", "sessionDots", "workDot", "breakDot", "wimOverlay", "wimCircle", "wimPhase", "wimCount", "wimRound", "wimAdvance"]
    static values = { autostartMode: String, autostartMinutes: Number }

    connect() {
        this.mode = null
        this.phase = "idle"
        this.durationMinutes = 0
        this.phaseDurationMs = 0
        this.remainingMs = 0
        this.running = false
        this.tickTimer = null
        this.breathTimer = null
        this.finishTimer = null
        this.endsAt = null
        this.round = 0
        this.totalRounds = 0
        this.retentionMs = 0
        this.retentionStart = null
        this.wimStartedAt = null

        this.restoreState()
        this.maybeAutostart()
        this.syncUi()
    }

    // Deep-link entry (/zen?timer=…, from a link or an old Meditate /
    // Pomodoro tile bookmark): explicit intent wins over any restored session. The
    // query param is stripped so a plain reload doesn't relaunch the timer.
    maybeAutostart() {
        const mode = this.hasAutostartModeValue ? this.autostartModeValue : ""
        if (!mode) return

        this.stripAutostartParam()
        this.reset()
        this.mode = mode

        if (mode === "wimhof") {
            this.startWimHof(WIMHOF_ROUNDS)
            return
        }

        const fallback = mode === "pomodoro" ? 25 : 10
        const minutes = (this.hasAutostartMinutesValue && this.autostartMinutesValue > 0) ? this.autostartMinutesValue : fallback
        this.durationMinutes = minutes
        this.startPhase("work", minutes * 60 * 1000)
    }

    stripAutostartParam() {
        try {
            const url = new URL(window.location.href)
            if (!url.searchParams.has("timer")) return
            url.searchParams.delete("timer")
            url.searchParams.delete("minutes")
            window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`)
        } catch (_error) {
            // History API unavailable — harmless; the timer still starts.
        }
    }

    disconnect() {
        this.stopTick()
        this.stopBreath()
        if (this.finishTimer) clearTimeout(this.finishTimer)
        this.persistState()
    }

    pick(event) {
        event.preventDefault()
        unlockChime()
        const { mode, minutes, rounds } = event.currentTarget.dataset
        this.mode = mode
        if (mode === "wimhof") {
            this.startWimHof(rounds ? parseInt(rounds, 10) : WIMHOF_ROUNDS)
            return
        }
        this.durationMinutes = parseInt(minutes, 10)
        this.startPhase("work", this.durationMinutes * 60 * 1000)
    }

    startPhase(phase, durationMs) {
        this.phase = phase
        this.phaseDurationMs = durationMs
        this.remainingMs = durationMs
        this.running = true
        this.endsAt = Date.now() + durationMs
        this.startTick()
        if (this.mode === "meditation" && phase === "work") this.startBreath()
        this.syncUi()
        this.persistState()

        if (this.mode === "pomodoro" && phase === "work") this.ping("/zen/pomodoro/started")
    }

    togglePause(event) {
        event.preventDefault()
        if (this.phase === "idle") return
        if (this.running) {
            this.pause()
        } else {
            this.resume()
        }
    }

    pause() {
        if (!this.running) return
        if (this.mode === "wimhof" && this.phase === "retention") {
            this.retentionMs = Date.now() - this.retentionStart
            this.retentionStart = null
        } else {
            this.remainingMs = Math.max(0, this.endsAt - Date.now())
        }
        this.running = false
        this.endsAt = null
        this.stopTick()
        this.stopBreath()
        this.syncUi()
        this.persistState()
    }

    resume() {
        if (this.phase === "idle" || this.running) return
        this.running = true
        if (this.mode === "wimhof" && this.phase === "retention") {
            this.retentionStart = Date.now() - (this.retentionMs || 0)
        } else {
            this.endsAt = Date.now() + this.remainingMs
        }
        this.startTick()
        if (this.mode === "meditation" && this.phase === "work") this.startBreath()
        this.syncUi()
        this.persistState()
    }

    end(event) {
        if (event) event.preventDefault()
        this.reset()
    }

    startTick() {
        this.stopTick()
        this.tickTimer = setInterval(() => this.onTick(), TICK_MS)
    }

    stopTick() {
        if (this.tickTimer) clearInterval(this.tickTimer)
        this.tickTimer = null
    }

    onTick() {
        if (this.mode === "wimhof") {
            this.wimTick()
            return
        }
        if (!this.running || !this.endsAt) return

        this.remainingMs = Math.max(0, this.endsAt - Date.now())
        this.syncUi()

        if (this.remainingMs <= 0) {
            this.onPhaseComplete()
        }
    }

    onPhaseComplete() {
        this.stopTick()
        this.running = false
        this.endsAt = null
        this.chime()

        if (this.phase !== "work") {
            this.reset()
            return
        }

        this.logSession()

        if (this.mode === "pomodoro") {
            let soundPlaying = !!document.querySelector(".zen-sound-trigger.is-playing")
            this.ping(`/zen/pomodoro/completed?sound=${soundPlaying}`)
            this.startPhase("break", POMODORO_BREAK_MS)
            return
        }

        this.finishGently()
    }

    // Brief "done" moment (finish copy for meditation) before returning to idle.
    finishGently() {
        this.phase = "done"
        this.stopBreath()
        localStorage.removeItem(STORAGE_KEY)

        if (this.mode === "meditation" && this.hasBreathPhaseTarget) {
            this.breathPhaseTarget.textContent = MEDITATION_FINISH_COPY
            if (this.hasBreathRemainingTarget) this.breathRemainingTarget.textContent = ""
        }
        if (this.hasLabelTarget) this.labelTarget.textContent = "Done"
        this.syncUi()

        this.finishTimer = setTimeout(() => this.reset(), FINISH_LINGER_MS)
    }

    // --- Wim Hof ---------------------------------------------------------
    // A short guided sequence, not persisted across reloads: each round runs
    // power breaths (a timed cadence), a full-exhale retention counted up
    // until the user taps Continue, then a fixed recovery hold. State is held
    // in memory only; a reload ends the session.

    startWimHof(rounds) {
        this.mode = "wimhof"
        this.totalRounds = rounds > 0 ? rounds : WIMHOF_ROUNDS
        this.round = 1
        this.wimStartedAt = Date.now()
        this.startBreaths()
    }

    startBreaths() {
        this.phase = "breaths"
        this.running = true
        this.phaseDurationMs = WIMHOF_BREATHS * WIMHOF_BREATH_MS
        this.remainingMs = this.phaseDurationMs
        this.endsAt = Date.now() + this.phaseDurationMs
        this.startTick()
        this.syncUi()
    }

    startRetention() {
        this.phase = "retention"
        this.running = true
        this.retentionStart = Date.now()
        this.retentionMs = 0
        this.endsAt = null
        this.startTick()
        this.syncUi()
    }

    startRecovery() {
        this.phase = "recovery"
        this.running = true
        this.phaseDurationMs = WIMHOF_RECOVERY_MS
        this.remainingMs = WIMHOF_RECOVERY_MS
        this.endsAt = Date.now() + WIMHOF_RECOVERY_MS
        this.startTick()
        this.syncUi()
    }

    // Tap Continue during retention to move on to the recovery hold.
    wimAdvance(event) {
        if (event) event.preventDefault()
        if (this.mode !== "wimhof" || this.phase !== "retention") return
        this.startRecovery()
    }

    wimTick() {
        if (!this.running) return

        if (this.phase === "retention") {
            this.retentionMs = Date.now() - this.retentionStart
            this.syncUi()
            return
        }

        if (!this.endsAt) return
        this.remainingMs = Math.max(0, this.endsAt - Date.now())
        this.syncUi()
        if (this.remainingMs <= 0) this.wimPhaseComplete()
    }

    wimPhaseComplete() {
        if (this.phase === "breaths") {
            this.startRetention()
        } else if (this.phase === "recovery") {
            this.nextRoundOrFinish()
        }
    }

    nextRoundOrFinish() {
        if (this.round < this.totalRounds) {
            this.round += 1
            this.startBreaths()
            return
        }
        this.wimFinish()
    }

    wimFinish() {
        this.chime()
        this.stopTick()
        this.running = false
        this.endsAt = null
        this.durationMinutes = Math.max(1, Math.round((Date.now() - this.wimStartedAt) / 60000))
        this.logSession()
        this.finishGently()
    }

    chime() {
        if (timerChimeEnabled()) playChime()
    }

    logSession() {
        fetch("/zen/sessions", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
            },
            body: JSON.stringify({ kind: this.mode, duration_minutes: this.durationMinutes })
        }).catch(() => {})
    }

    ping(url) {
        fetch(url, {
            method: "POST",
            headers: {
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
            }
        }).catch(() => {})
    }

    reset() {
        this.mode = null
        this.phase = "idle"
        this.durationMinutes = 0
        this.phaseDurationMs = 0
        this.remainingMs = 0
        this.running = false
        this.endsAt = null
        this.round = 0
        this.totalRounds = 0
        this.retentionMs = 0
        this.retentionStart = null
        this.wimStartedAt = null
        this.stopTick()
        this.stopBreath()
        if (this.finishTimer) clearTimeout(this.finishTimer)
        this.finishTimer = null
        localStorage.removeItem(STORAGE_KEY)
        this.syncUi()
    }

    startBreath() {
        this.stopBreath()
        this.breathIndex = 0
        this.setBreathPhase(BREATH_PHASES[0])
        this.breathTimer = setInterval(() => {
            this.breathIndex = (this.breathIndex + 1) % BREATH_PHASES.length
            this.setBreathPhase(BREATH_PHASES[this.breathIndex])
        }, BREATH_STEP_MS)
    }

    stopBreath() {
        if (this.breathTimer) clearInterval(this.breathTimer)
        this.breathTimer = null
    }

    setBreathPhase(text) {
        if (this.hasBreathPhaseTarget) this.breathPhaseTarget.textContent = text
    }

    restoreState() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY)
            if (!raw) return

            const state = JSON.parse(raw)
            if (!state || !state.phase || state.phase === "idle") return

            this.mode = state.mode || "pomodoro"
            this.phase = state.phase
            this.durationMinutes = state.durationMinutes || 25
            this.phaseDurationMs = state.phaseDurationMs || (this.phase === "break" ? POMODORO_BREAK_MS : this.durationMinutes * 60 * 1000)
            this.running = !!state.running

            if (this.running && state.endsAt) {
                this.endsAt = state.endsAt
                this.remainingMs = Math.max(0, this.endsAt - Date.now())
                if (this.remainingMs <= 0) {
                    this.onPhaseComplete()
                    return
                }
                this.startTick()
                if (this.mode === "meditation" && this.phase === "work") this.startBreath()
            } else {
                this.remainingMs = state.remainingMs || this.durationMinutes * 60 * 1000
            }
        } catch (_error) {
            localStorage.removeItem(STORAGE_KEY)
        }
    }

    persistState() {
        // Wim Hof runs in memory only - a reload ends the session cleanly
        // rather than trying to resume mid-hold.
        if (this.mode === "wimhof") return

        if (this.phase === "idle" || this.phase === "done") {
            localStorage.removeItem(STORAGE_KEY)
            return
        }

        const state = {
            mode: this.mode,
            phase: this.phase,
            durationMinutes: this.durationMinutes,
            phaseDurationMs: this.phaseDurationMs,
            running: this.running,
            remainingMs: this.remainingMs,
            endsAt: this.endsAt
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    }

    modeLabel() {
        if (this.phase === "break") return "Break"
        if (this.mode === "meditation") return "Meditation"
        if (this.mode === "wimhof") return "Wim Hof"
        if (this.mode === "focus") return "Focus"
        return "Pomodoro"
    }

    // The header/status readout value: retention counts up, everything else
    // counts down.
    displayMs() {
        if (this.mode === "wimhof" && this.phase === "retention") return this.retentionMs || 0
        return this.remainingMs
    }

    syncUi() {
        const active = this.phase !== "idle" && this.phase !== "done"
        const paused = active && !this.running

        if (this.hasTriggerTarget) {
            this.triggerTarget.classList.toggle("is-active", active)
            this.triggerTarget.classList.toggle("is-paused", paused)
            this.triggerTarget.classList.toggle("is-running", active && this.running)

            this.triggerTarget.title = active ? `${this.modeLabel()}: ${this.formatTime(this.displayMs())}` : "Timer"
            this.triggerTarget.setAttribute("aria-label", this.triggerTarget.title)
        }

        if (this.hasLabelTarget && this.phase !== "done") {
            this.labelTarget.textContent = active ? this.formatTime(this.displayMs()) : "Timer"
        }

        if (this.hasIconTarget) {
            const icon = this.iconTarget
            icon.classList.remove("bi-hourglass-split", "bi-hourglass-top", "bi-cup-hot", "bi-wind")
            if (this.phase === "break") {
                icon.classList.add("bi-cup-hot")
            } else if (active && (this.mode === "meditation" || this.mode === "wimhof")) {
                icon.classList.add("bi-wind")
            } else if (active && this.running) {
                icon.classList.add("bi-hourglass-top")
            } else {
                icon.classList.add("bi-hourglass-split")
            }
        }

        if (this.hasPickerTarget) this.pickerTarget.hidden = active
        if (this.hasControlsTarget) this.controlsTarget.hidden = !active

        if (this.hasStatusTarget && active) {
            this.statusTarget.textContent = this.mode === "wimhof"
                ? `${this.modeLabel()} - round ${this.round} of ${this.totalRounds}`
                : `${this.modeLabel()} - ${this.formatTime(this.remainingMs)}`
        }
        if (this.hasPauseButtonTarget) {
            this.pauseButtonTarget.textContent = paused ? "Resume" : "Pause"
        }

        this.syncOverlay(active, paused)
    }

    syncOverlay(active, paused) {
        this.syncBreathOverlay(active, paused)
        this.syncSessionOverlay(active, paused)
        this.syncWimOverlay(active, paused)
    }

    syncBreathOverlay(active, paused) {
        if (!this.hasOverlayTarget) return

        const meditating = this.mode === "meditation" && (active || this.phase === "done")
        this.overlayTarget.hidden = !meditating
        this.overlayTarget.classList.toggle("is-paused", paused)

        if (!meditating) return

        if (paused) this.setBreathPhase("Paused")
        if (this.hasBreathRemainingTarget && this.phase !== "done") {
            this.breathRemainingTarget.textContent = this.formatTime(this.remainingMs)
        }
    }

    // Centered ring overlay for focus and pomodoro. Focus is a quiet
    // countdown with a filling ring; pomodoro shifts state (label, ring
    // color, phase dots) between work and break.
    syncSessionOverlay(active, paused) {
        if (!this.hasSessionOverlayTarget) return

        const timerMode = this.mode === "focus" || this.mode === "pomodoro"
        const visible = timerMode && (active || this.phase === "done")
        this.sessionOverlayTarget.hidden = !visible
        if (!visible) return

        const onBreak = this.phase === "break"
        const done = this.phase === "done"
        this.sessionOverlayTarget.classList.toggle("is-paused", paused)
        this.sessionOverlayTarget.classList.toggle("is-break", onBreak)

        if (this.hasSessionRemainingTarget) {
            this.sessionRemainingTarget.textContent = done ? "0:00" : this.formatTime(this.remainingMs)
        }
        if (this.hasSessionModeTarget) {
            this.sessionModeTarget.textContent = done ? "Done" : (paused ? "Paused" : (onBreak ? "Break" : "Focus"))
        }
        if (this.hasRingProgressTarget) {
            const total = this.phaseDurationMs || 1
            const progress = done ? 1 : Math.min(1, Math.max(0, 1 - this.remainingMs / total))
            this.ringProgressTarget.style.strokeDashoffset = (RING_CIRCUMFERENCE * (1 - progress)).toFixed(1)
        }
        if (this.hasSessionDotsTarget) {
            this.sessionDotsTarget.hidden = this.mode !== "pomodoro"
            if (this.mode === "pomodoro" && this.hasWorkDotTarget && this.hasBreakDotTarget) {
                this.workDotTarget.classList.toggle("is-current", this.phase === "work")
                this.workDotTarget.classList.toggle("is-done", onBreak || done)
                this.breakDotTarget.classList.toggle("is-current", onBreak)
            }
        }
    }

    // Wim Hof overlay: a single circle that pulses through the power breaths,
    // sits small (empty lungs) during retention, and full during recovery,
    // with a phase label, breath/time readout, round count and a Continue tap.
    syncWimOverlay(active, paused) {
        if (!this.hasWimOverlayTarget) return

        const done = this.phase === "done"
        const wim = this.mode === "wimhof" && (active || done)
        this.wimOverlayTarget.hidden = !wim
        if (!wim) return

        this.wimOverlayTarget.classList.toggle("is-paused", paused)

        if (this.hasWimCircleTarget) {
            const circle = this.wimCircleTarget
            circle.classList.toggle("is-breathing", this.phase === "breaths" && !paused)
            circle.classList.toggle("is-empty", this.phase === "retention")
            circle.classList.toggle("is-full", this.phase === "recovery")
        }

        if (this.hasWimPhaseTarget) {
            this.wimPhaseTarget.textContent = done
                ? WIMHOF_FINISH_COPY
                : (paused ? "Paused" : (WIMHOF_PHASE_COPY[this.phase] || ""))
        }

        if (this.hasWimCountTarget) {
            this.wimCountTarget.textContent = done ? "" : this.wimCountText()
        }

        if (this.hasWimRoundTarget) {
            this.wimRoundTarget.textContent = done ? "" : `Round ${this.round} of ${this.totalRounds}`
        }

        if (this.hasWimAdvanceTarget) {
            this.wimAdvanceTarget.hidden = !(this.phase === "retention" && !paused)
        }
    }

    wimCountText() {
        if (this.phase === "breaths") {
            const elapsed = this.phaseDurationMs - this.remainingMs
            const n = Math.min(WIMHOF_BREATHS, Math.floor(elapsed / WIMHOF_BREATH_MS) + 1)
            return `Breath ${n} of ${WIMHOF_BREATHS}`
        }
        if (this.phase === "retention") return this.formatTime(this.retentionMs || 0)
        if (this.phase === "recovery") return this.formatTime(this.remainingMs)
        return ""
    }

    formatTime(ms) {
        const totalSec = Math.ceil(ms / 1000)
        const min = Math.floor(totalSec / 60)
        const sec = totalSec % 60
        return `${min}:${sec.toString().padStart(2, "0")}`
    }
}
